/**
 * Q&A Review Queue API service — admin review of auto-generated drafts
 * from verified Issue Analysis RCAs.
 */

import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';

export interface QaQueueEntry {
  id: number;
  analysis_id: number;
  ticket_key: string;
  question: string;
  answer: string;
  module: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  approved_answer_id: number | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export interface ApprovePayload {
  question?: string;
  answer?: string;
  category?: string;
  module?: string;
  extra_variants?: string[];
}

export const qaReviewQueueApi = {
  async list(
    status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending',
    limit = 50,
  ): Promise<{ entries: QaQueueEntry[]; total: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/qa-queue?status=${status}&limit=${limit}`,
      { headers },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to load queue (${response.status})`);
    }
    return response.json();
  },

  async approve(
    id: number,
    payload: ApprovePayload = {},
  ): Promise<{ queue_id: number; approved: true; trained_answer_id: number; reviewed_by: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/qa-queue/${id}/approve`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to approve (${response.status})`);
    }
    return response.json();
  },

  async reject(
    id: number,
    reason?: string,
  ): Promise<{ queue_id: number; rejected: true; reviewed_by: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/qa-queue/${id}/reject`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason: reason ?? null }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to reject (${response.status})`);
    }
    return response.json();
  },
};
